import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer with Swipe-to-close (LTR).
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  #startX = 0;
  #drawerElement = null;
  #containerWidth = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // القائمة الفعلية التي ستتحرك مع الإصبع
    this.#drawerElement = this.querySelector('.menu-drawer');
    this.#initSwipeEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

 #initSwipeEvents() {
    if (!this.#drawerElement) return;

    let ticking = false;
    let startY = 0;
    let isScrolling = false;
    let isSwiping = false;

    this.addEventListener('touchstart', (e) => {
      if (!this.isOpen) return;
      this.#startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isScrolling = false;
      isSwiping = false;
      
      // تعطيل الترانزيشن تماماً لبدء السحب الفوري
      this.#drawerElement.style.transition = 'none';
    }, { passive: true });

    this.addEventListener('touchmove', (e) => {
      if (!this.isOpen || isScrolling) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = currentX - this.#startX;
      const diffY = Math.abs(currentY - startY);

      if (!isSwiping && !isScrolling) {
        if (diffY > Math.abs(diffX) && diffY > 7) {
          isScrolling = true;
          return;
        } else if (Math.abs(diffX) > 7) {
          isSwiping = true;
          // إضافة كلاس لمنع أي تعارض مع CSS الثيم
          this.classList.add('is-swiping');
        }
      }

      if (isSwiping && diffX < 0) {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            // استخدام translate3d يضمن استخدام كرت الشاشة (سلاسة 60fps)
            this.#drawerElement.style.transform = `translate3d(${diffX}px, 0, 0)`;
            ticking = false;
          });
          ticking = true;
        }
        if (e.cancelable) e.preventDefault();
      }
    }, { passive: false });

    this.addEventListener('touchend', (e) => {
      this.classList.remove('is-swiping');
      if (!this.isOpen || isScrolling) return;

      const endX = e.changedTouches[0].clientX;
      const diff = endX - this.#startX;
      const swipePercentage = (Math.abs(diff) / this.#containerWidth) * 100;

      // العودة بنعومة فائقة باستخدام cubic-bezier احترافي
      this.#drawerElement.style.transition = 'transform 0.35s cubic-bezier(0.15, 0, 0.15, 1)';

      if (isSwiping && diff < 0 && swipePercentage >= 35) {
        this.#drawerElement.style.transform = `translate3d(-100%, 0, 0)`;
        this.close();
      } else {
        this.#drawerElement.style.transform = `translate3d(0, 0, 0)`;
      }

      // تنظيف الستايلات بعد انتهاء الأنميشن تماماً
      onAnimationEnd(this.#drawerElement, () => {
        if (!this.isOpen) this.#drawerElement.style.transform = '';
      });
    });
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    // تصفير أي ستايلات سحب قديمة قبل الفتح
    if (this.#drawerElement) {
      this.#drawerElement.style.transform = '';
      this.#drawerElement.style.transition = '';
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  #setupAnimatedElementListeners() {
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');
    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}